import type { Lot, State } from '../types';
import { apiUrl, log, showToast } from './common';
import { applyRemoteSnapshot } from '../lotOperations/snapshot';
import { triggerImmediatePoll } from '../polling';
import { resolveGlobalConfig } from '../api/config.js';

export async function submitRemoteLot(
  lot: any,
  retry: boolean,
  state: State,
  ensureAccessToken: (retry?: boolean) => Promise<string | null>,
  mapRemoteDocument: (data: any) => Lot | null | undefined,
  render: () => void,
  applyModeBanner: () => void,
  els: any,
  DEBUG: boolean
): Promise<boolean> {
  if (!lot || !lot.url) {
    showToast('Lot URL is required to submit to LotLingo.', { type: 'error' });
    return false;
  }
  let success = false;
  try {
    const token = await ensureAccessToken(Boolean(retry));
    if (!token) {
      showToast('Unable to acquire access token. Please sign in again.', { type: 'error' });
      return false;
    }
    const payload: any = { url: lot.url, source: lot.source || 'generic' };
    if (lot.title) payload.title = lot.title;
    if (lot.auctionId) payload.auctionId = lot.auctionId;
    if (lot.currentBid != null && lot.currentBid !== '')
      payload.currentBid = String(lot.currentBid);
    if (lot.lowSellValue != null && lot.lowSellValue !== '')
      payload.lowSellValue = String(lot.lowSellValue);
    if (lot.highSellValue != null && lot.highSellValue !== '')
      payload.highSellValue = String(lot.highSellValue);
    if (lot.notes) payload.description = lot.notes;
    if (lot.imageUrl) payload.images = [lot.imageUrl];
    if (lot.closeAt) {
      const closeDate = new Date(lot.closeAt);
      if (!Number.isNaN(closeDate.getTime())) payload.closeDateTime = closeDate.toISOString();
    }
    const body = { payload, status: lot.status || 'received' };
    const response = await fetch(apiUrl('extensions/scrapes'), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: 'Bearer ' + token },
      body: JSON.stringify(body),
    });
    if (response.status === 401 && !retry) {
      (state as any).extAccessToken = null;
      return await submitRemoteLot(
        lot,
        true,
        state,
        ensureAccessToken,
        mapRemoteDocument,
        render,
        applyModeBanner,
        els,
        DEBUG
      );
    }
    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || 'Failed to submit lot (' + response.status + ')');
    }
    const data = await response.json();
    const doc = data && data.document;
    const mapped = mapRemoteDocument(doc);
    if (mapped) {
      const existingIndex = state.lots.findIndex(item => item.id === mapped.id);
      if (existingIndex >= 0) state.lots[existingIndex] = mapped;
      else state.lots.unshift(mapped);
      (state as any).page = 1;
      render();
      applyRemoteSnapshot(state.lots);
    }
    if (els.form) els.form.reset();
    if (els.f && els.f.id) els.f.id.value = '';
    showToast('Lot submitted to LotLingo successfully.', { type: 'success' });
    void triggerImmediatePoll(resolveGlobalConfig(), mapRemoteDocument, render, 'submission');
    success = true;
  } catch (err: any) {
    const message = err && err.message ? err.message : String(err);
    showToast('Failed to submit lot: ' + message, { type: 'error' });
    log.error('Failed to submit remote lot', err);
  }
  applyModeBanner();
  return success;
}
